import { getApiUrl } from '@/lib/api-config';

export interface SendProposalEmailParams {
  proposalId: string;
  userId: string;
  companyId: string;
}

export interface SendProposalEmailResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Send proposal email to client via Document Sharing.
 * This service calls the backend API to:
 * 1. Freeze an immutable snapshot of the proposal (client info + latest attachment resolved at this moment)
 * 2. Email a link to that snapshot (View + Export to PDF), with the latest attachment attached if any
 * 3. Update proposal status to "Sent"
 *
 * `userId`/`companyId` are kept in the params for signature compatibility
 * with existing callers -- the backend derives both from the session, same
 * as before.
 */
export async function sendProposalEmail({
  proposalId,
}: SendProposalEmailParams): Promise<SendProposalEmailResponse> {
  try {
    const response = await fetch(getApiUrl(`api/documents/proposal/${proposalId}/share`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || errorData.message || 'Failed to send proposal email',
      };
    }

    const result = await response.json();
    if (!result.emailed) {
      return { success: false, error: 'No client email found for this proposal' };
    }
    return {
      success: true,
      message: 'Proposal sent successfully',
    };
  } catch (error: any) {
    console.error('Error sending proposal email:', error);
    return {
      success: false,
      error: error.message || 'Failed to send proposal email',
    };
  }
}

